import { useEffect, useState } from 'react';
import { DialogContent, Tabs, Tab, Tooltip, Button } from '@mui/material';
import FormDialogContainer from '../../../../Components/FormDialogContainer/FormDialogContainer';
import { DialogActionButtons } from '../../../../Components/DialogActionButtons/DialogActionButtons';
import { AttachmentsContainer } from './styles';
import { useTranslation } from 'react-i18next';
import { NotesBox } from '../../../../Components/NotesBox/NotesBox';
import { AttachmentsBox } from '../../../../Components/AttachmentsBox/AttachmentsBox';
import { Dropzone } from '../../../../Components/Dropzone/Dropzone';
import { GeneralInfo } from './GeneralInfo/GeneralInfo';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addNoteCandidateJob,
  editCandidateJob,
  editInterview,
  getCandidateJobFiles,
  getCandidateJobNotes,
  getInterviews,
  uploadCandidateJobFile,
} from '../../../../Services/CandidateJobs';
import toast from 'react-hot-toast';
import { generatePath, useNavigate } from 'react-router-dom';
import * as paths from '../../../../Routing/paths';
import { useAuthStore } from '../../../../Stores/useAuthStore';
import { InterviewCardList } from './InterviewCardList';

interface Props {
  open: boolean;
  handleClose: () => void;
  candidateJob: CandidateJob;
}

export const DetailedCard = ({ open, handleClose, candidateJob }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [files, setFiles] = useState<File[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [comments, setComments] = useState(candidateJob.generalData.comments ?? '');
  const [loadingFiles, setIsLoadingFiles] = useState(false);
  const [interviewsFiltered, setInterviewsFiltered] = useState<Interview[]>([]);

  const goToCandidateProfileLabel = t('planner.detailed_card.general_info.go_to_candidate_profile');
  const viewDetailsLabel = t('planner.detailed_card.general_info.view_details');

  const errorMessage = t('toast.errors.something_went_wrong');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const { data: interviews } = useQuery({
    queryKey: ['getInterviews', candidateJob.id],
    queryFn: async () => {
      const response = await getInterviews(candidateJob.id, { page: 1, count: 1000 });
      return response;
    },
    select: (data: any) => (Array.isArray(data.items) ? data.items : []),
  });

  const isUserAdminOrRecruiter = () => {
    return user?.isAdmin || user?.isRecruiter;
  };

  const { data: candidateJobNotes } = useQuery({
    queryKey: ['getCandidateJobNotes', candidateJob.id],
    queryFn: () =>
      getCandidateJobNotes(candidateJob.id, {
        page: 1,
        count: 100,
      }),
    enabled: !!candidateJob.id && isUserAdminOrRecruiter(),
  });

  const { mutate: createCandidateJobNoteMutation } = useMutation({
    mutationFn: (description: string) =>
      addNoteCandidateJob(candidateJob.id, { description, userId: user?.id! }),
    onError: () => {
      toast.error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getCandidateJobNotes', candidateJob.id] });
    },
  });

  const { data: candidateJobFiles, isLoading: isLoadingCandidateJobFiles } = useQuery({
    queryKey: ['getCandidateJobFiles', candidateJob.id],
    queryFn: () =>
      getCandidateJobFiles(`${candidateJob.id}`, {
        page: 1,
        count: 100,
      }),
    enabled: !!candidateJob.id,
  });

  const { mutate: uploadCandidateJobFileMutation } = useMutation({
    mutationFn: (data: FormData) => uploadCandidateJobFile(`${candidateJob.id}`, data),
    onError: () => {
      toast.error(errorMessage);
      setIsLoadingFiles(false);
    },
    onSuccess: () => {
      toast.success(t('toast.success.upload'));
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ['getCandidateJobFiles', candidateJob.id] });
      setIsLoadingFiles(false);
    },
  });

  const handleFileUpload = () => {
    if (files.length === 0) return;

    setIsLoadingFiles(true);
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });
    uploadCandidateJobFileMutation(formData);
  };

  const { mutate: editInterviewMutation } = useMutation({
    mutationFn: (data: InterviewModelIn) => editInterview(data),
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getCandidateJobs', candidateJob.jobId] });
    },
  });

  const { mutate: editCandidateJobMutation } = useMutation({
    mutationFn: (data: CandidateJob) => editCandidateJob(candidateJob?.id, data),
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
    onSuccess: () => {
      toast.success(t('toast.success.edit', { elem: 'Candidate Job ' }));
    },
  });

  const [descriptions, setDescriptions] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (interviews) {
      const internalClientDescription = interviews.find(
        (interview: any) => interview.title === 'Internal Client',
      )?.description;
      const pAndTDescription = interviews.find(
        (interview: any) => interview.title === 'P&T',
      )?.description;

      if (user?.isOperations || user?.isInternal) {
        setInterviewsFiltered(
          interviews.filter((interview: { title: string }) => interview.title !== 'Screening'),
        );
      } else {
        setInterviewsFiltered(interviews);
      }

      setDescriptions((prev) => ({
        ...prev,
        'Internal Client': internalClientDescription ?? '',
        'P&T': pAndTDescription ?? '',
      }));
    }
  }, [interviews, user]);

  const handleDescriptionChange = (title: string, description: string) => {
    setDescriptions((prev) => ({
      ...prev,
      [title]: description,
    }));
  };

  const handleSave = () => {
    if (isUserAdminOrRecruiter()) {
      const handleInterviewEdit = (title: string) => {
        const interview = interviews?.find((interview: Interview) => interview.title === title);
        if (interview) {
          editInterviewMutation({
            id: interview.id,
            title,
            date: new Date().toISOString(),
            description: descriptions[title] || '',
            userId: interview.user?.id ? Number(interview.user.id) : undefined,
          });
        }
      };

      handleInterviewEdit('Internal Client');
      handleInterviewEdit('P&T');
    }

    editCandidateJobMutation({
      ...candidateJob,
      generalData: {
        ...candidateJob.generalData,
        comments,
      },
    });

    handleClose();
  };

  return (
    <FormDialogContainer
      open={open}
      handleClose={handleClose}
      title={
        <span
          onClick={() =>
            navigate(
              generatePath(paths.CANDIDATE_DETAIL, {
                candidateId: `${candidateJob.candidateId}`,
              }),
            )
          }
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          {candidateJob.candidateName}
        </span>
      }
      disableBackdropClick
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '500px' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="tabs">
          <Tab label={t('planner.detailed_card.tabs.general')} />
          <Tab label={t('planner.detailed_card.tabs.interviews')} />
          {isUserAdminOrRecruiter() && <Tab label={t('planner.detailed_card.tabs.notes')} />}
          <Tab label={t('planner.detailed_card.tabs.attachments')} />
        </Tabs>
        {tabIndex === 0 && (
          <GeneralInfo comments={comments} setComments={setComments} candidate={candidateJob} />
        )}
        {tabIndex === 1 && interviewsFiltered && (
          <InterviewCardList
            interviews={interviewsFiltered}
            descriptions={descriptions}
            onDescriptionChange={handleDescriptionChange}
          />
        )}
        {isUserAdminOrRecruiter() && tabIndex === 2 && (
          <NotesBox
            containerWidth="540px"
            onSuccessEdit={() =>
              queryClient.invalidateQueries({ queryKey: ['getCandidateJobNotes', candidateJob.id] })
            }
            createNote={createCandidateJobNoteMutation}
            notes={candidateJobNotes}
          />
        )}
        {tabIndex === (isUserAdminOrRecruiter() ? 3 : 2) && (
          <AttachmentsContainer>
            <Dropzone
              onFileUpload={handleFileUpload}
              files={files}
              setFiles={setFiles}
              width="540px"
              loading={loadingFiles}
            />
            <AttachmentsBox
              files={candidateJobFiles ?? []}
              width={'540px'}
              isLoading={isLoadingCandidateJobFiles}
            />
          </AttachmentsContainer>
        )}
      </DialogContent>
      {isUserAdminOrRecruiter() && (
        <Tooltip title={goToCandidateProfileLabel} arrow placement="top">
          <Button
            onClick={() =>
              navigate(
                generatePath(paths.CANDIDATE_DETAIL, {
                  candidateId: `${candidateJob.candidateId}`,
                }),
              )
            }
            style={{
              fontSize: 14,
              color: 'gray',
              position: 'absolute',
              bottom: 15,
              left: 20,
              textDecoration: 'none',
            }}
          >
            {viewDetailsLabel}
          </Button>
        </Tooltip>
      )}
      <DialogActionButtons
        onCancel={() => {
          handleClose();
          setComments(candidateJob.generalData.comments ?? '');
        }}
        onConfirm={handleSave}
      />
    </FormDialogContainer>
  );
};
